// ============================================================================
// FSA REPORTS TAB
// ============================================================================
// Per-engagement list of NARRATIVE reports (Policy / Auditor's / Director's
// / CARO). Row action opens the Report Modal for placeholder editing +
// preview + print. Mirrors the Notes-tab shape so the patterns feel
// consistent across the workspace.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ActionMenu,
  type ActionMenuItem,
  MinimizedModalStrip,
  SyutGrid,
  type SyutGridColumn,
} from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useFsaReportsList } from 'hooks/api/fsa/useFsaReports';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { fsaReportService } from 'api/services/fsa/fsaReportService';
import FsaReportModal from 'components/modals/modules/fsa/FsaReportModal';
import FsaReportPdfPreviewModal from 'components/modals/modules/fsa/FsaReportPdfPreviewModal';
import type { ID } from 'api/types';
import type { ReportListItem } from 'api/types/fsa/reportData.types';

interface Props {
  engagementId: ID;
  tabId?: string;
  moduleName?: string;
}

const FsaReportsTab: React.FC<Props> = ({ engagementId, tabId, moduleName }) => {
  const { data: rows, loading, refetch } = useFsaReportsList(engagementId);
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const reportModalMinimizeRef = useRef<(() => void) | null>(null);

  const [openReport, setOpenReport] = useState<ReportListItem | null>(null);
  const [pdfReport, setPdfReport] = useState<ReportListItem | null>(null);

  // FSA-2.2 — include/exclude state. Two-step ConfirmDialog flow (mirrors
  // FsaNotesTab): pre-confirm → on 409 swap to "clear saved data?" confirm.
  const [pendingExclude, setPendingExclude] = useState<ReportListItem | null>(null);
  const [confirmClearFor, setConfirmClearFor] = useState<ReportListItem | null>(null);
  const [processingExclusionFor, setProcessingExclusionFor] = useState<ID | null>(null);

  const handleOpen = useCallback((row: ReportListItem) => {
    if (tabId) {
      const modalId = `${tabId}-fsa-report-${row.reportId}`;
      if (isMinimized(modalId)) {
        const m = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        m?.onRestore?.();
        return;
      }
    }
    if (openReport && openReport.reportId !== row.reportId && reportModalMinimizeRef.current) {
      reportModalMinimizeRef.current();
    }
    setOpenReport(row);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, openReport]);

  const handleRestoreModal = useCallback((reportId: ID) => {
    const row = rows.find(r => r.reportId === reportId);
    if (row) setOpenReport(row);
  }, [rows]);

  const handlePrint = useCallback((row: ReportListItem) => {
    setPdfReport(row);
  }, []);

  // FSA-2.2 — exclude / include flow (mirrors FsaNotesTab).
  const performExclude = useCallback(async (row: ReportListItem, clearSavedData: boolean) => {
    setProcessingExclusionFor(row.reportId);
    try {
      await fsaReportService.exclude(engagementId, row.reportId, { clearSavedData });
      notificationService.toast.success(`"${row.reportName}" marked Not Applicable.`);
      setPendingExclude(null);
      setConfirmClearFor(null);
      refetch();
    } catch (e: any) {
      const status = e?.response?.status;
      const errors: string[] = e?.response?.data?.errors ?? [];
      if (status === 409 && errors.includes('requires_clear_confirmation')) {
        setPendingExclude(null);
        setConfirmClearFor(row);
      } else {
        notificationService.toast.error(e?.response?.data?.message || e?.message || 'Failed to exclude report.');
      }
    } finally {
      setProcessingExclusionFor(null);
    }
  }, [engagementId, refetch]);

  const handleMarkExcluded = useCallback((row: ReportListItem) => {
    setPendingExclude(row);
  }, []);

  const handleMarkIncluded = useCallback(async (row: ReportListItem) => {
    setProcessingExclusionFor(row.reportId);
    try {
      await fsaReportService.include(engagementId, row.reportId);
      notificationService.toast.success(`"${row.reportName}" marked Applicable.`);
      refetch();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || e?.message || 'Failed to reinstate report.');
    } finally {
      setProcessingExclusionFor(null);
    }
  }, [engagementId, refetch]);

  const columns = useMemo<SyutGridColumn<any>[]>(() => [
    {
      id: 'reportName',
      header: 'Report',
      accessorKey: 'reportName',
      size: 320,
      cell: ({ row }: any) => {
        const r: ReportListItem = row.original;
        const muted = r.isExcluded ? { color: 'var(--text-secondary)', fontStyle: 'italic' as const } : undefined;
        return (
          <div style={muted}>
            <div style={{ fontWeight: r.isExcluded ? 500 : 600 }}>{r.reportName}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{r.reportCode}</div>
          </div>
        );
      },
    },
    {
      id: 'isExcluded',
      header: 'Include',
      size: 110,
      cell: ({ row }: any) => {
        const r: ReportListItem = row.original;
        return r.isExcluded
          ? <span style={chip('#f1f5f9', '#475569')} title="Skipped from consolidated FS PDF">✗ Excluded</span>
          : <span style={chip('#dcfce7', '#166534')}>✓ Included</span>;
      },
    },
    {
      id: 'coverage',
      header: 'Placeholders',
      size: 180,
      cell: ({ row }: any) => {
        const r: ReportListItem = row.original;
        if (r.totalPlaceholders === 0) {
          return <span style={chip('#f1f5f9', '#475569')}>—</span>;
        }
        const pct = r.totalPlaceholders === 0
          ? 100
          : Math.round((r.filledPlaceholders / r.totalPlaceholders) * 100);
        const allFilled = r.filledPlaceholders >= r.totalPlaceholders;
        return (
          <span style={chip(
            allFilled ? '#dcfce7' : pct >= 50 ? '#dbeafe' : '#fef3c7',
            allFilled ? '#166534' : pct >= 50 ? '#1e40af' : '#92400e',
          )} title={`${r.filledPlaceholders} of ${r.totalPlaceholders} placeholders resolve to a value`}>
            {r.filledPlaceholders} / {r.totalPlaceholders} ({pct}%)
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      size: 110,
      cell: ({ row }: any) => {
        const r: ReportListItem = row.original;
        if (r.isExcluded) return <span style={chip('#f1f5f9', '#475569')}>—</span>;
        if (!r.hasSavedData) return <span style={chip('#f1f5f9', '#475569')}>Not started</span>;
        if (r.statusCode === 'APPROVED')  return <span style={chip('#dcfce7', '#166534')}>Approved</span>;
        if (r.statusCode === 'SUBMITTED') return <span style={chip('#dbeafe', '#1e40af')}>Submitted</span>;
        return <span style={chip('#e0f2fe', '#0369a1')}>Draft</span>;
      },
    },
    {
      id: 'updatedAt',
      header: 'Last saved',
      size: 200,
      cell: ({ row }: any) => {
        const r: ReportListItem = row.original;
        if (!r.updatedAt) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
        return (
          <div>
            <div>{new Date(r.updatedAt).toLocaleString()}</div>
            {r.updatedByName && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>by {r.updatedByName}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 60,
      cell: ({ row }: any) => {
        const r: ReportListItem = row.original;
        const busy = processingExclusionFor === r.reportId;
        const items: ActionMenuItem[] = [
          {
            id: 'open',
            label: 'Open',
            icon: <IconSystem.edit size={14} />,
            onClick: () => handleOpen(r),
          },
          {
            id: 'print',
            label: 'Print preview',
            icon: <IconSystem.print size={14} />,
            onClick: () => handlePrint(r),
          },
        ];
        if (r.isExcluded) {
          items.push({
            id: 'include',
            label: 'Mark Included',
            icon: <IconSystem.check size={14} />,
            onClick: () => handleMarkIncluded(r),
            disabled: busy,
          });
        } else {
          items.push({
            id: 'exclude',
            label: 'Mark Excluded',
            icon: <IconSystem.x size={14} />,
            onClick: () => handleMarkExcluded(r),
            disabled: busy,
            variant: 'danger',
          });
        }
        items.push({
          id: 'refresh',
          label: 'Refresh',
          icon: <IconSystem.refresh size={14} />,
          onClick: () => refetch(),
        });
        return <ActionMenu items={items} />;
      },
    },
  ], [handleOpen, handlePrint, refetch, processingExclusionFor, handleMarkExcluded, handleMarkIncluded]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={toolbarStyle}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          {loading ? 'Loading…' : `${rows.length} report${rows.length === 1 ? '' : 's'}`}
        </div>
        {tabId && <MinimizedModalStrip tabId={tabId} />}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={refetch}
          title="Refresh"
          disabled={loading}
        >
          <IconSystem.refresh size={14} />
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <SyutGrid
          data={rows as any}
          columns={columns as any}
          enableSorting
          emptyMessage={loading ? 'Loading…' : 'No narrative reports under this template.'}
        />
      </div>

      <FsaReportModal
        isOpen={openReport != null}
        engagementId={engagementId}
        reportId={openReport?.reportId ?? null}
        reportHeader={openReport ? { code: openReport.reportCode, name: openReport.reportName } : undefined}
        onClose={() => setOpenReport(null)}
        onSaved={refetch}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        minimizeRef={reportModalMinimizeRef}
      />

      <FsaReportPdfPreviewModal
        isOpen={pdfReport != null}
        engagementId={engagementId}
        reportId={pdfReport?.reportId ?? null}
        reportLabel={pdfReport ? `${pdfReport.reportCode} · ${pdfReport.reportName}` : null}
        onClose={() => setPdfReport(null)}
      />

      {/* FSA-2.2 — pre-confirm Mark Excluded. Server checks for saved data
          and returns 409 → swaps to the second ConfirmDialog. */}
      <ConfirmDialog
        open={pendingExclude != null}
        title="Mark Report Not Applicable"
        message={
          pendingExclude
            ? `Skip "${pendingExclude.reportName}" from the consolidated FS PDF for this engagement?`
            : ''
        }
        confirmLabel="Mark Not Applicable"
        cancelLabel="Cancel"
        variant="danger"
        loading={processingExclusionFor === pendingExclude?.reportId}
        loadingMessage="Excluding…"
        onConfirm={() => pendingExclude && performExclude(pendingExclude, false)}
        onCancel={() => setPendingExclude(null)}
      />

      <ConfirmDialog
        open={confirmClearFor != null}
        title="Clear Saved Data First?"
        message={
          confirmClearFor
            ? `"${confirmClearFor.reportName}" has saved placeholder content. Excluding will reset it. Continue?`
            : ''
        }
        confirmLabel="Clear & Exclude"
        cancelLabel="Keep Data"
        variant="danger"
        loading={processingExclusionFor === confirmClearFor?.reportId}
        loadingMessage="Clearing…"
        onConfirm={() => confirmClearFor && performExclude(confirmClearFor, true)}
        onCancel={() => setConfirmClearFor(null)}
      />
    </div>
  );
};

// ───── styles ─────────────────────────────────────────────────────────────

const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const chip = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  background: bg, color, fontSize: '0.7rem', fontWeight: 600,
});

export default FsaReportsTab;
